import React, { useState, useEffect, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/Discounts.css';

const AdminSidebar = memo(AdminSidebarRaw);

const Discounts = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [subSubCategories, setSubSubCategories] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtering & Search
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState(null);
    const [filterSubCategory, setFilterSubCategory] = useState(null);
    const [filterSpecialty, setFilterSpecialty] = useState(null);
    const [filterSeason, setFilterSeason] = useState(null);

    // Modal State
    const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [discountFormData, setDiscountFormData] = useState({
        discount_type: '',
        discount_value: '',
        discount_start_date: '',
        discount_end_date: '',
        discount_is_active: 1,
        discount_id: null
    });

    const [selectedDiscounts, setSelectedDiscounts] = useState([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);

    // View Mode: 'discounts' or 'offers'
    const [viewMode, setViewMode] = useState('discounts');
    const [offers, setOffers] = useState([]);
    const [selectedOffers, setSelectedOffers] = useState([]);

    // Offer Modal State
    const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
    const [editingOffer, setEditingOffer] = useState(null);
    const [offerFormData, setOfferFormData] = useState({
        productID: '',
        offer_qty: '',
        offer_price: '',
        from_date: '',
        to_date: '',
        active: 1
    });
    const [isOfferStatusConfirmOpen, setIsOfferStatusConfirmOpen] = useState(false);
    const [pendingOfferStatus, setPendingOfferStatus] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };
        try {
            setLoading(true);
            const [discRes, catRes, subRes, subSubRes, seasonRes, offersRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/admin/discounts`, { headers }),
                axios.get(`${API_BASE_URL}/admin/categories`, { headers }),
                axios.get(`${API_BASE_URL}/admin/subcategories`, { headers }),
                axios.get(`${API_BASE_URL}/admin/subsubcategories`, { headers }),
                axios.get(`${API_BASE_URL}/admin/seasons`, { headers }),
                axios.get(`${API_BASE_URL}/admin/offers`, { headers })
            ]);
            setProducts(discRes.data);
            setCategories(catRes.data);
            setSubCategories(subRes.data);
            setSubSubCategories(subSubRes.data);
            setSeasons(seasonRes.data);
            setOffers(offersRes.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
        }
    };

    const filteredProducts = useMemo(() => {
        return products.filter(d => {
            const searchLower = searchTerm.toLowerCase();
            const itemName = `${d.productName || ''} ${d.variationName || ''} ${d.sizeName || ''} ${d.colorName || ''}`.toLowerCase();
            const matchesSearch = itemName.includes(searchLower);

            const matchesCat = !filterCategory || d.catID === filterCategory;
            const matchesSub = !filterSubCategory || d.subCatID === filterSubCategory;
            const matchesSpecialty = !filterSpecialty || d.subSubID === filterSpecialty;
            const matchesSeason = !filterSeason || String(d.season) === String(filterSeason);

            return matchesSearch && matchesCat && matchesSub && matchesSpecialty && matchesSeason;
        });
    }, [products, searchTerm, filterCategory, filterSubCategory, filterSpecialty, filterSeason]);

    const filteredOffers = useMemo(() => {
        return offers.filter(o => {
            const searchLower = searchTerm.toLowerCase();
            const itemName = `${o.productName || ''} ${o.categoryName || ''} ${o.subCategoryName || ''} ${o.subSubCategoryName || ''} ${o.seasonName || ''}`.toLowerCase();
            return itemName.includes(searchLower);
        });
    }, [offers, searchTerm]);

    const handleOpenDiscountModal = (discount) => {
        setEditingProduct(discount);
        setDiscountFormData({
            discount_type: discount.discount_type || 'percentage',
            discount_value: discount.discount_value || '',
            discount_start_date: discount.start_date ? discount.start_date.substring(0, 10) : '',
            discount_end_date: discount.end_date ? discount.end_date.substring(0, 10) : '',
            discount_is_active: discount.is_active !== undefined ? discount.is_active : 1,
            discount_id: discount.id || null
        });
        setIsDiscountModalOpen(true);
    };

    const handleSaveDiscount = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                product_id: editingProduct.product_id,
                variation_id: editingProduct.variation_id,
                discount_type: discountFormData.discount_type,
                discount_value: discountFormData.discount_value,
                start_date: discountFormData.discount_start_date,
                end_date: discountFormData.discount_end_date,
                is_active: discountFormData.discount_is_active
            };

            const adminToken = localStorage.getItem('adminToken');
            const headers = { Authorization: `Bearer ${adminToken}` };
            if (discountFormData.discount_id) {
                await axios.put(`${API_BASE_URL}/admin/discounts/${discountFormData.discount_id}`, payload, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/admin/discounts`, payload, { headers });
            }
            setIsDiscountModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving discount:', error);
            alert('Failed to save discount');
        }
    };

    const toggleSelectDiscount = (id) => {
        setSelectedDiscounts(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedDiscounts.length === filteredProducts.length) {
            setSelectedDiscounts([]);
        } else {
            setSelectedDiscounts(filteredProducts.map(d => d.id));
        }
    };

    const handleBulkDelete = async () => {
        setIsDeleteModalOpen(true);
    };

    const handleOpenOfferModal = (offer) => {
        setEditingOffer(offer);
        setOfferFormData({
            productID: offer.productID,
            offer_qty: offer.offer_qty || '',
            offer_price: offer.offer_price || '',
            from_date: offer.from_date ? offer.from_date.substring(0, 10) : '',
            to_date: offer.to_date ? offer.to_date.substring(0, 10) : '',
            active: offer.active !== undefined ? offer.active : 1
        });
        setIsOfferModalOpen(true);
    };

    const handleSaveOffer = async (e) => {
        e.preventDefault();
        try {
            const adminToken = localStorage.getItem('adminToken');
            const headers = { Authorization: `Bearer ${adminToken}` };
            const payload = { ...offerFormData };

            if (editingOffer && editingOffer.id) {
                await axios.put(`${API_BASE_URL}/admin/offers/${editingOffer.id}`, payload, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/admin/offers`, payload, { headers });
            }
            setIsOfferModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving offer:', error);
            alert('Failed to save offer');
        }
    };

    const confirmBulkDelete = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            if (viewMode === 'discounts') {
                await Promise.all(selectedDiscounts.map(id => axios.delete(`${API_BASE_URL}/admin/discounts/${id}`, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                })));
                setSelectedDiscounts([]);
            } else {
                await Promise.all(selectedOffers.map(id => axios.delete(`${API_BASE_URL}/admin/offers/${id}`, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                })));
                setSelectedOffers([]);
            }
            fetchData();
            setIsDeleteModalOpen(false);
        } catch (error) {
            console.error('Error in bulk delete:', error);
            alert('Failed to delete items');
        }
    };

    return (
        <div className="discounts-container">
            <AdminSidebar />
            <main className="discounts-main">
                <header className="discounts-hero">
                </header>

                <div className="discounts-controls">
                    <div className="table-actions">
                        <div className="view-mode-switch">
                            <button
                                className={`mode-btn ${viewMode === 'discounts' ? 'active' : ''}`}
                                onClick={() => setViewMode('discounts')}
                            >
                                Discounts
                            </button>
                            <button
                                className={`mode-btn ${viewMode === 'offers' ? 'active' : ''}`}
                                onClick={() => setViewMode('offers')}
                            >
                                Offers
                            </button>
                        </div>
                        <div className="discounts-search-box">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <input
                                type="text"
                                placeholder={`Search ${viewMode}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {viewMode === 'discounts' && (
                            <>
                                <div className="filter-item">
                                    <select
                                        value={filterCategory || ''}
                                        onChange={(e) => {
                                            setFilterCategory(e.target.value ? parseInt(e.target.value) : null);
                                            setFilterSubCategory(null);
                                            setFilterSpecialty(null);
                                        }}
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>

                                <div className="filter-item">
                                    <select
                                        value={filterSubCategory || ''}
                                        onChange={(e) => {
                                            setFilterSubCategory(e.target.value ? parseInt(e.target.value) : null);
                                            setFilterSpecialty(null);
                                        }}
                                        disabled={!filterCategory}
                                    >
                                        <option value="">All Sub-Categories</option>
                                        {subCategories
                                            .filter(sc => sc.category_id === filterCategory)
                                            .map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                                    </select>
                                </div>

                                <div className="filter-item">
                                    <select
                                        value={filterSpecialty || ''}
                                        onChange={(e) => setFilterSpecialty(e.target.value ? parseInt(e.target.value) : null)}
                                        disabled={!filterSubCategory}
                                    >
                                        <option value="">All Specialties</option>
                                        {subSubCategories
                                            .filter(ssc => ssc.subcategory_id === filterSubCategory)
                                            .map(ssc => <option key={ssc.id} value={ssc.id}>{ssc.name}</option>)}
                                    </select>
                                </div>

                                <div className="filter-item">
                                    <select value={filterSeason || ''} onChange={(e) => setFilterSeason(e.target.value || null)}>
                                        <option value="">All Seasons</option>
                                        {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="discounts-table-wrapper">
                    {viewMode === 'discounts' ? (
                        <table className="discounts-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={filteredProducts.length > 0 && selectedDiscounts.length === filteredProducts.length}
                                            onChange={toggleSelectAll}
                                            className="table-checkbox"
                                        />
                                    </th>
                                    <th>Photo</th>
                                    <th style={{ minWidth: '250px' }}>Product</th>
                                    <th>Category</th>
                                    <th style={{ minWidth: '180px' }}>Season</th>
                                    <th style={{ minWidth: '150px' }}>Price</th>
                                    <th style={{ minWidth: '180px' }}>Discount</th>
                                    <th style={{ width: '100%' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="8" className="no-results">Loading products...</td></tr>
                                ) : filteredProducts.length > 0 ? (
                                    filteredProducts.map(d => (
                                        <tr key={d.id} className={selectedDiscounts.includes(d.id) ? 'selected-row' : ''}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDiscounts.includes(d.id)}
                                                    onChange={() => toggleSelectDiscount(d.id)}
                                                    className="table-checkbox"
                                                />
                                            </td>
                                            <td>
                                                <div className="product-img-mini">
                                                    {d.variationPhoto || d.productPhoto || d.fallbackPhoto ? (
                                                        <img src={`${API_BASE_URL}${d.variationPhoto || d.productPhoto || d.fallbackPhoto}`} alt={d.productName} />
                                                    ) : (
                                                        <div className="img-placeholder" />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="product-name-sku">
                                                    <span className="p-name">{d.productName}</span>
                                                    {d.variationName && (
                                                        <span className="p-sku" style={{ color: 'var(--accent-primary)', fontWeight: '500' }}>
                                                            {d.variationName}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="cat-pill-group">
                                                    <span className="cat-pill main">{d.categoryName || 'Uncategorized'}</span>
                                                    {d.subCategoryName && <span className="cat-pill sub">{d.subCategoryName}</span>}
                                                    {d.specialtyName && <span className="cat-pill specialty">{d.specialtyName}</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '0.9rem', color: '#10b981', fontWeight: '500' }}>
                                                    {d.seasonName ? `${d.seasonName} ${d.seasonYear || ''}` : '—'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <span className="p-price" style={{ textDecoration: 'line-through', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                                        ${parseFloat(d.productPrice || 0).toFixed(2)}
                                                    </span>
                                                    <span className="p-price" style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                                                        ${(d.discount_type === 'percentage'
                                                            ? parseFloat(d.productPrice || 0) * (1 - parseFloat(d.discount_value || 0) / 100)
                                                            : Math.max(0, parseFloat(d.productPrice || 0) - parseFloat(d.discount_value || 0))
                                                        ).toFixed(2)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <span className="discount-badge">
                                                        {d.discount_type === 'percentage' ? `-${d.discount_value}%` : `-$${d.discount_value}`}
                                                    </span>
                                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                                        {d.start_date ? new Date(d.start_date).toLocaleDateString() : '—'}
                                                        {' to '}
                                                        {d.end_date ? new Date(d.end_date).toLocaleDateString() : '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${d.is_active ? 'active' : 'inactive'}`}>
                                                    {d.is_active ? 'Active' : 'Paused'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="8" className="no-results">No discounts found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="discounts-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={filteredOffers.length > 0 && selectedOffers.length === filteredOffers.length}
                                            onChange={() => {
                                                if (selectedOffers.length === filteredOffers.length) setSelectedOffers([]);
                                                else setSelectedOffers(filteredOffers.map(o => o.id));
                                            }}
                                            className="table-checkbox"
                                        />
                                    </th>
                                    <th style={{ width: '50px' }}>Photo</th>
                                    <th style={{ minWidth: '160px' }}>Product</th>
                                    <th style={{ minWidth: '140px' }}>Category</th>
                                    <th style={{ width: '130px' }}>Season</th>
                                    <th style={{ width: '100px' }}>Qty</th>
                                    <th style={{ width: '80px' }}>Price</th>
                                    <th style={{ width: '95px' }}>Start</th>
                                    <th style={{ width: '95px' }}>End</th>
                                    <th style={{ width: '90px' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="10" className="no-results">Loading offers...</td></tr>
                                ) : filteredOffers.length > 0 ? (
                                    filteredOffers.map(o => (
                                        <tr key={o.id} className={selectedOffers.includes(o.id) ? 'selected-row' : ''}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedOffers.includes(o.id)}
                                                    onChange={() => {
                                                        setSelectedOffers(prev => prev.includes(o.id) ? prev.filter(id => id !== o.id) : [...prev, o.id]);
                                                    }}
                                                    className="table-checkbox"
                                                />
                                            </td>
                                            <td>
                                                <div className="product-img-mini">
                                                    {o.variationPhoto || o.productPhoto ? (
                                                        <img src={`${API_BASE_URL}${o.variationPhoto || o.productPhoto}`} alt={o.productName} />
                                                    ) : (
                                                        <div className="img-placeholder" />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="p-name">{o.productName}</span>
                                            </td>
                                            <td>
                                                <div className="cat-pill-group">
                                                    <span className="cat-pill main">{o.categoryName || 'Uncategorized'}</span>
                                                    {o.subCategoryName && <span className="cat-pill sub">{o.subCategoryName}</span>}
                                                    {o.subSubCategoryName && <span className="cat-pill specialty">{o.subSubCategoryName}</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '0.9rem', color: '#10b981', fontWeight: '500' }}>
                                                    {o.seasonName ? `${o.seasonName} ${o.seasonYear || ''}` : '—'}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ fontWeight: '700', color: 'var(--primary)' }}>{o.offer_qty} units</span>
                                            </td>
                                            <td>
                                                <span className="p-price" style={{ color: 'var(--success)' }}>${parseFloat(o.offer_price).toFixed(2)}</span>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '0.85rem' }}>{o.from_date ? new Date(o.from_date).toLocaleDateString() : '—'}</span>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '0.85rem' }}>{o.to_date ? new Date(o.to_date).toLocaleDateString() : '—'}</span>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${o.active ? 'active' : 'inactive'}`}>
                                                    {o.active ? 'Active' : 'Paused'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="10" className="no-results">No offers found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <footer className="persistent-footer">
                    <div className="footer-content">
                        <div className="footer-section info">
                            <span className="footer-count">
                                <strong>{viewMode === 'discounts' ? selectedDiscounts.length : selectedOffers.length}</strong> Selected / <strong>{viewMode === 'discounts' ? filteredProducts.length : filteredOffers.length}</strong> Total
                            </span>
                        </div>

                        <div className="footer-section main-actions">
                            <button className="footer-btn refresh" onClick={() => {
                                setSearchTerm('');
                                setFilterCategory(null);
                                setFilterSeason(null);
                                setSelectedDiscounts([]);
                                setSelectedOffers([]);
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10"></polyline>
                                    <path d="M3.51 15a9 9 0 1 0 .49-4.5"></path>
                                </svg>
                            </button>

                            <div className="action-divider"></div>

                            <button className="footer-btn new" onClick={() => navigate('/admin/products')}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                New
                            </button>



                            <div className="action-divider"></div>

                            <button
                                className="footer-btn edit"
                                disabled={viewMode === 'discounts' ? selectedDiscounts.length !== 1 : selectedOffers.length !== 1}
                                onClick={() => {
                                    if (viewMode === 'discounts') {
                                        const d = products.find(p => p.id === selectedDiscounts[0]);
                                        if (d) handleOpenDiscountModal(d);
                                    } else {
                                        const o = offers.find(off => off.id === selectedOffers[0]);
                                        if (o) handleOpenOfferModal(o);
                                    }
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit
                            </button>

                            <button
                                className="footer-btn delete"
                                disabled={viewMode === 'discounts' ? selectedDiscounts.length === 0 : selectedOffers.length === 0}
                                onClick={handleBulkDelete}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                                Delete
                            </button>

                            <div className="action-divider"></div>

                            <button className="footer-btn cancel" onClick={() => {
                                setSelectedDiscounts([]);
                                setSelectedOffers([]);
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </footer>
            </main>

            {isDiscountModalOpen && (
                <div className="modal-overlay" style={{ backdropFilter: 'blur(10px)', background: 'rgba(15, 23, 42, 0.4)' }}>
                    <div className="product-modal" style={{
                        maxWidth: '500px',
                        borderRadius: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden',
                        background: '#ffffff'
                    }}>
                        <div className="modal-header" style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                            padding: '20px 24px',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px'
                        }}>
                            <div className="product-img-mini" style={{ width: '56px', height: '56px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                {(editingProduct?.variationPhoto || editingProduct?.productPhoto || editingProduct?.fallbackPhoto) ? (
                                    <img src={`${API_BASE_URL}${editingProduct.variationPhoto || editingProduct.productPhoto || editingProduct.fallbackPhoto}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', background: '#f1f5f9' }} />
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 2px', color: '#0f172a' }}>Edit Discount</h2>
                                <p style={{ margin: '0', fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
                                    {editingProduct?.productName} {editingProduct?.variationName && <span style={{ color: '#3b82f6' }}>- {editingProduct.variationName}</span>}
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleSaveDiscount} className="product-form">
                            <div className="form-main" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>Type</label>
                                        <select
                                            value={discountFormData.discount_type}
                                            onChange={(e) => setDiscountFormData({ ...discountFormData, discount_type: e.target.value })}
                                            required
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', fontSize: '0.9rem' }}
                                        >
                                            <option value="percentage">Percentage (%)</option>
                                            <option value="fixed">Fixed Amount ($)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>Value</label>
                                        <input
                                            type="number"
                                            value={discountFormData.discount_value}
                                            onChange={(e) => setDiscountFormData({ ...discountFormData, discount_value: e.target.value })}
                                            placeholder="0.00"
                                            required
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '700', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>Start Date</label>
                                        <input
                                            type="date"
                                            value={discountFormData.discount_start_date}
                                            onChange={(e) => setDiscountFormData({ ...discountFormData, discount_start_date: e.target.value })}
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>End Date</label>
                                        <input
                                            type="date"
                                            value={discountFormData.discount_end_date}
                                            onChange={(e) => setDiscountFormData({ ...discountFormData, discount_end_date: e.target.value })}
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>

                                <div style={{
                                    background: '#f8fafc',
                                    padding: '12px 16px',
                                    borderRadius: '14px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>Active Status</span>
                                    </div>
                                    <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                        <input
                                            type="checkbox"
                                            checked={discountFormData.discount_is_active}
                                            onChange={(e) => {
                                                setPendingStatus(e.target.checked ? 1 : 0);
                                                setIsStatusConfirmOpen(true);
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                        />
                                        <span className="slider round" style={{
                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                            backgroundColor: discountFormData.discount_is_active ? '#3b82f6' : '#cbd5e1',
                                            transition: '.4s', borderRadius: '34px'
                                        }}>
                                            <span style={{
                                                position: 'absolute', height: '16px', width: '16px', left: '4px', bottom: '4px',
                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                transform: discountFormData.discount_is_active ? 'translateX(20px)' : 'translateX(0)'
                                            }}></span>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-footer" style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                                padding: '16px 24px',
                                background: '#f8fafc',
                                borderTop: '1px solid #f1f5f9'
                            }}>
                                <button type="button" className="footer-btn" style={{
                                    background: 'transparent',
                                    color: '#64748b',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                }} onClick={() => setIsDiscountModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="footer-btn" style={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 28px',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 8px 16px -4px rgba(59, 130, 246, 0.4)',
                                    cursor: 'pointer'
                                }}>
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isOfferModalOpen && (
                <div className="modal-overlay" style={{ backdropFilter: 'blur(10px)', background: 'rgba(15, 23, 42, 0.4)' }}>
                    <div className="product-modal" style={{
                        maxWidth: '500px',
                        borderRadius: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden',
                        background: '#ffffff'
                    }}>
                        <div className="modal-header" style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%)',
                            padding: '20px 24px',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px'
                        }}>
                            <div className="product-img-mini" style={{ width: '56px', height: '56px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                {editingOffer?.productPhoto ? (
                                    <img src={`${API_BASE_URL}${editingOffer.productPhoto}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', background: '#f1f5f9' }} />
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 2px', color: '#0f172a' }}>Edit Offer</h2>
                                <p style={{ margin: '0', fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
                                    {editingOffer?.productName}
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleSaveOffer} className="product-form">
                            <div className="form-main" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>Quantity Threshold</label>
                                        <input
                                            type="number"
                                            value={offerFormData.offer_qty}
                                            onChange={(e) => setOfferFormData({ ...offerFormData, offer_qty: e.target.value })}
                                            placeholder="Min units"
                                            required
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '700', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>Offer Price (Per Unit)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={offerFormData.offer_price}
                                            onChange={(e) => setOfferFormData({ ...offerFormData, offer_price: e.target.value })}
                                            placeholder="0.00"
                                            required
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '700', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>Start Date</label>
                                        <input
                                            type="date"
                                            value={offerFormData.from_date}
                                            onChange={(e) => setOfferFormData({ ...offerFormData, from_date: e.target.value })}
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '6px' }}>End Date</label>
                                        <input
                                            type="date"
                                            value={offerFormData.to_date}
                                            onChange={(e) => setOfferFormData({ ...offerFormData, to_date: e.target.value })}
                                            style={{ padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>

                                <div style={{
                                    background: '#f8fafc',
                                    padding: '12px 16px',
                                    borderRadius: '14px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>Offer Status</span>
                                    </div>
                                    <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                        <input
                                            type="checkbox"
                                            checked={offerFormData.active}
                                            onChange={(e) => {
                                                setPendingOfferStatus(e.target.checked ? 1 : 0);
                                                setIsOfferStatusConfirmOpen(true);
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                        />
                                        <span className="slider round" style={{
                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                            backgroundColor: offerFormData.active ? '#10b981' : '#cbd5e1',
                                            transition: '.4s', borderRadius: '34px'
                                        }}>
                                            <span style={{
                                                position: 'absolute', height: '16px', width: '16px', left: '4px', bottom: '4px',
                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                transform: offerFormData.active ? 'translateX(20px)' : 'translateX(0)'
                                            }}></span>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-footer" style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                                padding: '16px 24px',
                                background: '#f8fafc',
                                borderTop: '1px solid #f1f5f9'
                            }}>
                                <button type="button" className="footer-btn" style={{
                                    background: 'transparent',
                                    color: '#64748b',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                }} onClick={() => setIsOfferModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="footer-btn" style={{
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 28px',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 8px 16px -4px rgba(16, 185, 129, 0.4)',
                                    cursor: 'pointer'
                                }}>
                                    Update Offer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isOfferStatusConfirmOpen && (
                <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(15, 23, 42, 0.4)', zIndex: 1100 }}>
                    <div className="confirm-modal">
                        <div className="confirm-header warning">
                            <div className="confirm-icon warning">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                            </div>
                            <h2 className="confirm-title">Confirm Offer Status</h2>
                        </div>
                        <div className="confirm-main">
                            <p className="confirm-main-title">Are you sure?</p>
                            <p className="confirm-main-text">You are about to change the active status of this offer.</p>
                        </div>
                        <div className="confirm-footer">
                            <button className="confirm-btn-cancel" onClick={() => setIsOfferStatusConfirmOpen(false)}>Cancel</button>
                            <button className="confirm-btn-warning" onClick={() => {
                                setOfferFormData({ ...offerFormData, active: pendingOfferStatus });
                                setIsOfferStatusConfirmOpen(false);
                            }}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}
            {isDeleteModalOpen && (
                <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(15, 23, 42, 0.4)' }}>
                    <div className="confirm-modal">
                        <div className="confirm-header danger">
                            <div className="confirm-icon danger">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                            </div>
                            <h2 className="confirm-title">Confirm Deletion</h2>
                        </div>
                        <div className="confirm-main">
                            <p className="confirm-main-title">
                                Are you absolutely sure?
                            </p>
                            <p className="confirm-main-text">
                                You are about to permanently remove <strong>{viewMode === 'discounts' ? selectedDiscounts.length : selectedOffers.length}</strong> selected {viewMode}. This action cannot be undone.
                            </p>
                        </div>
                        <div className="confirm-footer">
                            <button className="confirm-btn-cancel" onClick={() => setIsDeleteModalOpen(false)}>
                                Nevermind
                            </button>
                            <button className="confirm-btn-danger" onClick={confirmBulkDelete}>
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isStatusConfirmOpen && (
                <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(15, 23, 42, 0.4)', zIndex: 1100 }}>
                    <div className="confirm-modal">
                        <div className="confirm-header warning">
                            <div className="confirm-icon warning">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                            </div>
                            <h2 className="confirm-title">Confirm Status Change</h2>
                        </div>
                        <div className="confirm-main">
                            <p className="confirm-main-title">
                                Are you sure?
                            </p>
                            <p className="confirm-main-text">
                                You are about to change the active status of this discount.
                            </p>
                        </div>
                        <div className="confirm-footer">
                            <button className="confirm-btn-cancel" onClick={() => setIsStatusConfirmOpen(false)}>
                                Cancel
                            </button>
                            <button className="confirm-btn-warning" onClick={() => {
                                setDiscountFormData({ ...discountFormData, discount_is_active: pendingStatus });
                                setIsStatusConfirmOpen(false);
                            }}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Discounts;


